import { useState, useEffect, useRef } from 'react';
import { getIceServers } from '../iceServers';

export function SupervisorPage({ socket, token, agent, onLogout }) {
  const [requests, setRequests] = useState([]);
  const [watching, setWatching] = useState(null); // { code, agentUsername, description }
  const [hasStream, setHasStream] = useState(false);
  const [privateMessages, setPrivateMessages] = useState([]);
  const [privateInput, setPrivateInput] = useState('');

  const videoRef = useRef(null);
  const peerRef = useRef(null);
  const streamRef = useRef(null);
  const watchingRef = useRef(null);
  const iceCandidatesBuffer = useRef([]);

  useEffect(() => {
    if (!socket || !token) return;
    socket.emit('supervisor:hello', { token });

    socket.on('requests:updated', (list) => {
      setRequests(list.filter(r => r.status === 'sharing'));
    });

    socket.on('supervisor:watching', ({ code, agentUsername, description }) => {
      watchingRef.current = code;
      setWatching({ code, agentUsername, description });
      setPrivateMessages([{ text: `Počeli ste da pratite sesiju ${code}`, system: true }]);
    });

    socket.on('supervisor:webrtc-offer', async ({ offer }) => {
      if (peerRef.current) peerRef.current.close();
      peerRef.current = null;
      const peer = new RTCPeerConnection({ iceServers: await getIceServers(socket) });
      peerRef.current = peer;

      peer.ontrack = (e) => {
        streamRef.current = e.streams[0];
        setHasStream(true);
        if (videoRef.current) {
          videoRef.current.srcObject = e.streams[0];
          videoRef.current.play().catch(() => {});
        }
      };
      peer.onicecandidate = (e) => {
        if (e.candidate) {
          socket.emit('supervisor:webrtc-ice', { code: watchingRef.current, candidate: e.candidate });
        }
      };

      await peer.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await peer.createAnswer();
      await peer.setLocalDescription(answer);

      for (const candidate of iceCandidatesBuffer.current) {
        try { await peer.addIceCandidate(new RTCIceCandidate(candidate)); } catch (e) {}
      }
      iceCandidatesBuffer.current = [];

      socket.emit('supervisor:webrtc-answer', { code: watchingRef.current, answer });
    });

    socket.on('supervisor:webrtc-ice', async ({ candidate }) => {
      if (peerRef.current && peerRef.current.remoteDescription) {
        try { await peerRef.current.addIceCandidate(new RTCIceCandidate(candidate)); } catch (e) {}
      } else {
        iceCandidatesBuffer.current.push(candidate);
      }
    });

    socket.on('supervisor:private-message', ({ text, from }) => {
      setPrivateMessages(prev => [...prev, { text, from }]);
    });

    socket.on('supervisor:disconnected', () => {
      setPrivateMessages(prev => [...prev, { text: 'Klijent se diskonektovao', system: true }]);
      cleanupStream();
    });

    socket.on('session:ended', () => {
      setPrivateMessages(prev => [...prev, { text: 'Sesija je završena', system: true }]);
      cleanupStream();
      watchingRef.current = null;
      setWatching(null);
    });

    return () => {
      socket.off('requests:updated');
      socket.off('supervisor:watching');
      socket.off('supervisor:webrtc-offer');
      socket.off('supervisor:webrtc-ice');
      socket.off('supervisor:private-message');
      socket.off('supervisor:disconnected');
      socket.off('session:ended');
    };
  }, [socket, token]);

  const cleanupStream = () => {
    if (peerRef.current) { peerRef.current.close(); peerRef.current = null; }
    streamRef.current = null;
    setHasStream(false);
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const watchSession = (code) => {
    if (watching) stopWatching();
    socket.emit('supervisor:watch-session', { code, token });
  };

  const stopWatching = () => {
    if (watchingRef.current) {
      socket.emit('supervisor:stop-watching', { code: watchingRef.current });
    }
    cleanupStream();
    watchingRef.current = null;
    setWatching(null);
    setPrivateMessages([]);
  };

  const sendPrivateMessage = () => {
    if (!privateInput.trim() || !watching) return;
    socket.emit('supervisor:private-message', { code: watching.code, text: privateInput, from: 'supervisor' });
    setPrivateMessages(prev => [...prev, { text: privateInput, from: 'supervisor' }]);
    setPrivateInput('');
  };

  const formatWait = (isoTime) => {
    if (!isoTime) return '—';
    const seconds = Math.floor((Date.now() - new Date(isoTime).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s`;
    return `${Math.floor(seconds / 60)}m`;
  };

  const s = {
    wrap: { display: 'flex', height: '100vh', fontFamily: 'sans-serif', background: '#f0f0f0' },
    sidebar: { width: 300, flexShrink: 0, background: '#fff', borderRight: '1px solid #e5e5e5', display: 'flex', flexDirection: 'column', overflowY: 'auto' },
    topbar: { padding: '14px 16px', borderBottom: '1px solid #e5e5e5', display: 'flex', flexDirection: 'column', gap: 4 },
    title: { fontSize: 16, fontWeight: 700, color: '#111' },
    subtitle: { fontSize: 12, color: '#888' },
    btn: (v) => ({
      padding: '7px 14px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500,
      background: v === 'primary' ? '#185FA5' : v === 'danger' ? '#E24B4A' : '#f0f0f0',
      color: v ? '#fff' : '#333'
    }),
    listWrap: { flex: 1, overflowY: 'auto', padding: 10 },
    sessionCard: (active) => ({
      background: active ? '#F0F6FC' : '#fafafa', border: active ? '1.5px solid #185FA5' : '1px solid #eee',
      borderRadius: 8, padding: 10, marginBottom: 8, cursor: 'pointer'
    }),
    sessionCode: { fontFamily: 'monospace', fontWeight: 700, fontSize: 14 },
    sessionMeta: { fontSize: 11, color: '#888', marginTop: 3 },
    main: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, overflow: 'hidden' },
    videoWrap: { flex: 1, position: 'relative', background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', minHeight: 0 },
    video: { maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain', display: 'block' },
    placeholder: { color: '#888', fontSize: 14, textAlign: 'center' },
    liveBadge: { position: 'absolute', top: 10, left: 10, background: '#E24B4A', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 4 },
    watchInfo: { position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 12, padding: '6px 10px', borderRadius: 6 },
    chatPanel: { height: 260, background: '#fff', borderTop: '1px solid #e5e5e5', display: 'flex', flexDirection: 'column' },
    chatHeader: { padding: '8px 12px', borderBottom: '1px solid #eee', fontSize: 12, fontWeight: 600, color: '#8A5A1F', background: '#FAF3E8' },
    chatMessages: { flex: 1, overflowY: 'auto', padding: 10, display: 'flex', flexDirection: 'column', gap: 6 },
    chatMsg: (from) => ({
      maxWidth: '75%', padding: '7px 10px', borderRadius: 8, fontSize: 13,
      alignSelf: from === 'agent' ? 'flex-start' : from === 'supervisor' ? 'flex-end' : 'center',
      background: from === 'agent' ? '#E6F1FB' : from === 'supervisor' ? '#FAF3E8' : 'transparent',
      color: from ? '#222' : '#999', fontStyle: from ? 'normal' : 'italic'
    }),
    chatInputRow: { padding: 8, borderTop: '1px solid #eee', display: 'flex', gap: 6 },
    chatInput: { flex: 1, padding: '7px 10px', borderRadius: 7, border: '1px solid #ddd', fontSize: 13, outline: 'none' },
    empty: { padding: 30, textAlign: 'center', color: '#999', fontSize: 13 }
  };

  return (
    <div style={s.wrap}>
      <div style={s.sidebar}>
        <div style={s.topbar}>
          <div style={s.title}>Supervizor</div>
          <div style={s.subtitle}>{agent?.fullName || agent?.username}</div>
          <button style={{ ...s.btn(), marginTop: 8 }} onClick={onLogout}>Odjavi se</button>
        </div>
        <div style={{ padding: '10px 16px 0', fontSize: 12, fontWeight: 600, color: '#888', textTransform: 'uppercase' }}>
          Aktivne sesije ({requests.length})
        </div>
        <div style={s.listWrap}>
          {requests.length === 0 && <div style={s.empty}>Trenutno nema aktivnih sesija.</div>}
          {requests.map(r => (
            <div key={r.code} style={s.sessionCard(watching?.code === r.code)} onClick={() => watchSession(r.code)}>
              <div style={s.sessionCode}>{r.code}</div>
              <div style={s.sessionMeta}>Agent: {r.agentUsername || '—'}</div>
              {r.description && <div style={s.sessionMeta}>{r.description}</div>}
              <div style={s.sessionMeta}>U toku: {formatWait(r.sharingStartedAt || r.requestedAt)}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={s.main}>
        <div style={s.videoWrap}>
          {watching ? (
            <>
              <video ref={videoRef} style={{ ...s.video, display: hasStream ? 'block' : 'none' }} autoPlay playsInline />
              {hasStream && <div style={s.liveBadge}>● PRATI SE</div>}
              <div style={s.watchInfo}>
                Sesija {watching.code} · Agent: {watching.agentUsername}
                <button style={{ ...s.btn('danger'), marginLeft: 10, padding: '3px 8px', fontSize: 11 }} onClick={stopWatching}>Prestani</button>
              </div>
              {!hasStream && <div style={s.placeholder}>Čeka se video stream...</div>}
            </>
          ) : (
            <div style={s.placeholder}>Izaberite sesiju sa leve strane da počnete posmatranje</div>
          )}
        </div>

        {watching && (
          <div style={s.chatPanel}>
            <div style={s.chatHeader}>🔒 Privatni chat sa agentom {watching.agentUsername} (klijent ovo ne vidi)</div>
            <div style={s.chatMessages}>
              {privateMessages.map((m, i) => (
                <div key={i} style={s.chatMsg(m.from)}>
                  {m.system ? m.text : <><strong>{m.from === 'supervisor' ? 'Vi' : 'Agent'}:</strong> {m.text}</>}
                </div>
              ))}
            </div>
            <div style={s.chatInputRow}>
              <input
                style={s.chatInput}
                value={privateInput}
                onChange={e => setPrivateInput(e.target.value)}
                placeholder="Poruka agentu (privatno)..."
                onKeyDown={e => e.key === 'Enter' && sendPrivateMessage()}
              />
              <button style={s.btn('primary')} onClick={sendPrivateMessage}>→</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
